<template>
  <SkeletonForDashboard v-if="loading === true" />

  <div v-else class="dash-body">

    <!-- MOBILE: stacked single column -->
    <div class="dash-mobile" v-if="$vuetify.display.smAndDown">

      <!-- Timer -->
      <div class="dash-card dash-timer-card" v-if="showTimer && userData.id == selectedUser.id">
        <Timer :showHomeCard="showTimer" @workInProgress="showTimer = false" :showTimer="showTimer" />
      </div>

      <!-- Stat cards 2x2 -->
      <div class="dash-stats-grid">
        <div v-for="item in dashBoardCardData" :key="item.code" class="dash-stat-card">
          <div class="dash-stat-card__accent"></div>
          <div class="dash-stat-card__icon" :style="{ background: item.bgcolor }">
            <img :src="item.src" width="20" height="20" />
          </div>
          <div class="dash-stat-card__content">
            <span class="dash-stat-card__label">{{ item.name }}</span>
            <div class="dash-stat-card__value" v-if="item.code == 'production'">
              <span class="val-main">{{ timeConvert(item.name, item.hour) || 0 }}</span>
              <span class="val-sep">/</span>
              <span class="val-est">{{ timeConvert(item.name, item.estimate) || 0 }}</span>
              <span class="val-unit">hrs</span>
            </div>
            <div class="dash-stat-card__value" v-else>
              <span class="val-main">{{ timeConvert(item.name, item.hour) || 0 }}</span>
              <span class="val-unit">hrs</span>
            </div>
          </div>
          <div class="dash-stat-card__bubble" :style="{ background: item.bgcolor }"></div>
        </div>
      </div>

      <!-- Timesheet -->
      <div class="dash-card">
        <TimeSheet
          :timeSheetData="timeSheetData"
          :userId="selectedUser.id"
          :id="userId"
          :fromDate="moment(dates[0]).format('YYYY-MM-DD')"
          :toDate="moment(dates[1] || dates[0]).format('YYYY-MM-DD')"
        />
      </div>

      <!-- Assigned to me -->
      <div class="dash-card">
        <AssignedToMe :userId="selectedUser.id" />
      </div>

      <!-- Timezone -->
      <div class="dash-card dash-tz-card">
        <TimeZoneClock />
      </div>

      <!-- Insights -->
      <div class="dash-card">
        <ErrorTrends :userId="selectedUser.id" />
      </div>
      <div class="dash-card">
        <DoYouKnow :userId="selectedUser.id" />
      </div>
    </div>

    <!-- TABLET: 2 columns -->
    <div class="dash-tablet" v-else-if="$vuetify.display.md">
      <div class="dash-tablet__left">

        <div class="dash-card dash-timer-card" v-if="showTimer && userData.id == selectedUser.id">
          <!-- <Timer :showHomeCard="showTimer" @workInProgress="showTimer = false" :showTimer="showTimer" /> -->
        </div>

        <div class="dash-card dash-timesheet-card">
          <TimeSheet
            :timeSheetData="timeSheetData"
            :userId="selectedUser.id"
            :id="userId"
            :fromDate="moment(dates[0]).format('YYYY-MM-DD')"
            :toDate="moment(dates[1] || dates[0]).format('YYYY-MM-DD')"
          />
        </div>
      </div>

      <div class="dash-tablet__right">
        <div class="dash-stats-grid">
          <div v-for="item in dashBoardCardData" :key="item.code" class="dash-stat-card">
            <div class="dash-stat-card__accent"></div>
            <div class="dash-stat-card__icon" :style="{ background: item.bgcolor }">
              <img :src="item.src" width="20" height="20" />
            </div>
            <div class="dash-stat-card__content">
              <span class="dash-stat-card__label">{{ item.name }}</span>
              <div class="dash-stat-card__value" v-if="item.code == 'production'">
                <span class="val-main">{{ timeConvert(item.name, item.hour) || 0 }}</span>
                <span class="val-sep">/</span>
                <span class="val-est">{{ timeConvert(item.name, item.estimate) || 0 }}</span>
                <span class="val-unit">hrs</span>
              </div>
              <div class="dash-stat-card__value" v-else>
                <span class="val-main">{{ timeConvert(item.name, item.hour) || 0 }}</span>
                <span class="val-unit">hrs</span>
              </div>
            </div>
            <div class="dash-stat-card__bubble" :style="{ background: item.bgcolor }"></div>
          </div>
        </div>

        <div class="dash-card">
          <AssignedToMe :userId="selectedUser.id" />
        </div>

        <div class="dash-card dash-tz-card">
          <TimeZoneClock />
        </div>

        <div class="dash-card">
          <ErrorTrends :userId="selectedUser.id" />
        </div>
        <div class="dash-card">
          <DoYouKnow :userId="selectedUser.id" />
        </div>
      </div>
    </div>

    <!-- DESKTOP: 3 columns -->
    <div class="dash-desktop" :class="[desktopLayoutClass, showTimer ? 'dash-desktop--timer-active' : 'dash-desktop--shift-active']" v-else>

      <!-- COL 1: Assigned To Me -->
      <div class="dash-col dash-col--assigned">
        <div class="dash-panel-tools">
          <button
            class="dash-panel-tool"
            :class="{ 'dash-panel-tool--active': dashboardWidthMode === 'assigned-expanded' }"
            @click="setDashboardWidthMode('assigned-expanded')"
            :title="dashboardWidthMode === 'assigned-expanded' ? 'Restore assigned column' : 'Expand assigned column'"
          >
            <v-icon size="15">{{ dashboardWidthMode === 'assigned-expanded' ? 'mdi-arrow-collapse-horizontal' : 'mdi-arrow-expand-horizontal' }}</v-icon>
          </button>
        </div>
        <AssignedToMe :userId="selectedUser.id" />
      </div>

      <!-- COL 2: Timer + Timesheet -->
        
      <div class="dash-col dash-col--center">
        <div class="dash-card dash-timer-card" v-if="showTimer && userData.id == selectedUser.id">
          <Timer :showHomeCard="showTimer" @workInProgress="showTimer = false" :showTimer="showTimer" />
        </div>

        <div class="timer-wrapper" v-else>
          <TimeZoneClock />
        </div>

        <div
          class="dash-card dash-timesheet-card"
          :class="{ 'dash-timesheet-card--full': !showTimer }"
        >
          <div class="dash-panel-tools dash-panel-tools--timesheet">
            <button
              class="dash-panel-tool"
              :class="{ 'dash-panel-tool--active': dashboardWidthMode === 'timesheet-expanded' }"
              @click="setDashboardWidthMode('timesheet-expanded')"
              :title="dashboardWidthMode === 'timesheet-expanded' ? 'Restore timesheet column' : 'Expand timesheet column'"
            >
              <v-icon size="15">{{ dashboardWidthMode === 'timesheet-expanded' ? 'mdi-arrow-collapse-horizontal' : 'mdi-arrow-expand-horizontal' }}</v-icon>
            </button>
          </div>
          <TimeSheet
            :timeSheetData="timeSheetData"
            :userId="selectedUser.id"
            :id="userId"
            :fromDate="moment(dates[0]).format('YYYY-MM-DD')"
            :toDate="moment(dates[1] || dates[0]).format('YYYY-MM-DD')"
          />
        </div>
      </div>

      <!-- COL 3: Stats + Insights -->
      <div class="dash-col dash-col--right">
        <div class="dash-stats-panel">
          <div class="dash-stats-grid" :class="{ 'dash-stats-grid--vertical': showTimer }">
            <v-hover v-slot="{ isHovering, props }" open-delay="100" v-for="item in dashBoardCardData" :key="item.code">
              <div v-bind="props" class="dash-stat-card" :class="{ 'dash-stat-card--hovered': isHovering, 'dash-stat-card--vertical': showTimer }">
                <div class="dash-stat-card__accent"></div>
                <div class="dash-stat-card__icon" :style="{ background: item.bgcolor }">
                  <img :src="item.src" width="22" height="22" />
                </div>
                <div class="dash-stat-card__content">
                  <span class="dash-stat-card__label">
                    {{ item.name }}
                    <v-tooltip activator="parent" location="bottom">{{ item.toolTip }}</v-tooltip>
                  </span>
                  <div class="dash-stat-card__value" v-if="item.code == 'production'">
                    <span class="val-main">{{ timeConvert(item.name, item.hour) || 0 }}</span>
                    <span class="val-sep">/</span>
                    <span class="val-est">{{ timeConvert(item.name, item.estimate) || 0 }}</span>
                    <span class="val-unit">hrs</span>
                  </div>
                  <div class="dash-stat-card__value" v-else>
                    <span class="val-main">{{ timeConvert(item.name, item.hour) || 0 }}</span>
                    <span class="val-unit">hrs</span>
                  </div>
                </div>
                <div class="dash-stat-card__bubble" :style="{ background: item.bgcolor }"></div>
              </div>
            </v-hover>
          </div>
        </div>

        <div class="dash-insights" v-if="!isInsightHidden">
          <!-- <div class="dash-tz-card dash-card">
            <TimeZoneClock />
          </div> -->
          <div class="dash-card dash-insight-card">
            <ErrorTrends :userId="selectedUser.id" />
          </div>
          <div class="dash-card dash-insight-card">
            <DoYouKnow :userId="selectedUser.id" />
          </div>
        </div>
      </div>

    </div>
  </div>

</template>

<script>
import TimeSheet from "./TimeSheet.vue";
import lodash from "lodash";
import ErrorTrends from "./ErrorTrends.vue";
import commonService from "@/common.service";
// import Calendar from "primevue/calendar";
import UserService from "@/api/service/UserService";
import Timer from "@/menus/Timer.vue";
// import NextAction from "@/modules/IssueV2/NextAction.vue";
import TimeSheetService from "@/api/service/TimeSheet/TimeSheetService";
import moment from "moment-timezone";
import DoYouKnow from "./DoYouKnow.vue";
import AssignedToMe from "@/modules/issues/AssignedToMe.vue";
import WorkTimingService from "@/api/service/WorkTiming/WorkTimingService";
import SkeletonForDashboard from "./SkeletonForDashboard.vue";
import IssueService from "@/api/service/Issue/IssueService";
import TimeZoneClock from "@/components/TimeZoneClock.vue";

export default {
  components: {
    SkeletonForDashboard,
    TimeSheet,
    // Calendar,
    AssignedToMe,
    // NextAction,
    ErrorTrends,
    Timer,
    DoYouKnow,
    TimeZoneClock,
  },
  data() {
    return {
      loading: true,
      liveTime: "",
      clockTimer: null,
      workTiming: { name: null },
      users: [],
      dates: [],
      userId: null,
      selectedUser: {},
      dialog: false,
      showTimer: false,
      reloadTheActivity: false,
      size: {},
      defaltUser: {},
      showDialog: false,
      issueId: null,
      doubleCounter: null,
      columns: [],
      userData: null,
      timeSheetData: [],
      totalHours: [],
      showEdit: false,
      date: new Date(),
      workTimingTrack:{},
      remainingTime: 0,
      overtimeMs: 0,
      isOvertime: false,
      workShiftData:[],
      dashboardWidthMode: null,
      timer: null,
      radius: 54,
      dashBoardCardData: [
        {
          name: "Productivity",
          toolTip: "Productivity",
          hour: 0,
          code: "production",
          bgcolor: "#F8ECCE",
          estimate: 0,
          src: require("@/assets/image/clock.svg"),
        },
        {
          name: "Meeting",
          toolTip: "Meeting",
          hour: 0,
          code: "meeting",
          bgcolor: "#D7EAF4",
          src: require("@/assets/image/meet-now.png"),
        },
        {
          name: "Break",
          toolTip: "Break",
          code: "break",
          hour: 0,
          bgcolor: "#F9E3F5",
          src: require("@/assets/image/Break Time.png"),
        },
        {
          name: "IBT",
          toolTip: "In-Between Time",
          code: "others",
          hour: 0,
          bgcolor: "#F8F6CE",
          estimate: 0,
          src: require("@/assets/image/color.svg"),
        },
      ],
    };
  },
  watch: {
    getInBetweenTime() {
      this.reloadTheTimerData();
    },
    reloadTheData() {
      this.reloadTheTimerData();
    },
    getRefresh() {
      this.getTimeCardDetails();
    },
    refreshProject() {
      if (this.$store.getters.getStartIssueId) {
        localStorage.setItem("currentIsseId", this.$store.getters.getStartIssueId);
        this.getIssueTimerData();
      }
    },
    showTimer(val) {
      if (val) {
        // When timer is running, don't auto-expand timesheet — keep default layout
        // so ErrorTrends and DoYouKnow remain visible
        this.dashboardWidthMode = null;
      } else {
        // reset to default size when timer is closed
        this.dashboardWidthMode = null;
      }
    },
    getTimerHide() {
      if (this.getTimerHide) {
        this.showTimer = false;
      } else {
        this.checkTimer();
      }
    },
    getTimesheetDate(val) {
      if (val) {
        this.dates = [new Date(val), new Date(val)];
        this.getTimeSheetData();
        this.getTimeCardDetails();
      }
    },
    getSelectedDashboardUser(val) {
      if (val) {
        this.selectedUser = val;
        this.getTimeSheetData();
        this.getTimeCardDetails();
      }
    },
    changeLocalStore() {
      console.log("timer");
    },
  },
  created() {
    let timerData = JSON.parse(localStorage.getItem("timer"));
    this.showTimer = !!(timerData && timerData.type == "issue");
    // Don't auto-expand timesheet when timer is running — keep insights visible

    let $val = localStorage.getItem("_WORK_TIMING_");
    if ($val) this.workTiming = JSON.parse($val);

    this.dates.push(new Date());
    this.userData = JSON.parse(localStorage.getItem("user_details"));
    this.selectedUser = this.userData;
    this.defaltUser = JSON.parse(localStorage.getItem("user_details"));
    this.loadColumns();
    this.workTimingTrack = JSON.parse(localStorage.getItem("_WORK_TIMING_"));
    this.download = true;
    this.search = true;
    this.checkbox = true;
    this.getWorkTiming();
    // this.startTimer();
    this.getTimeCardDetails();
    this.getTimeSheetData();
    this.toShowEdit();
    this.getReporterUserData();
    if (this.$route.query && (this.$route.query.reference_num || this.$route.query.name)) {
      this.goIssueView(this.$route.query);
    }
    this.liveTime = moment().format("hh:mm:ss A");
    this.clockTimer = setInterval(() => {
      this.liveTime = moment().format("hh:mm:ss A");
    }, 1000);
  },
  beforeUnmount() {
    clearInterval(this.clockTimer);
  },

  methods: {
    toShowEdit() {
      let userRoleCode = this.userData.roles.code;
      this.showEdit = !["PL", "PA", "SPA"].includes(userRoleCode);
    },
    async getWorkTiming() {
        try {
          let res = await WorkTimingService.findOne(this.workTimingTrack.id);
          if (res) {
            console.log(res,"work timing res");
            this.workShiftData = res;
            this.startTimer()
          }
        } catch (e) {
          console.log(e);
        }
    },
    startTimer() {
      this.timer = setInterval(() => {
        const now = new Date();
        const time = this.workShiftData[0].end_time; // "HH:mm"
        const [hours, minutes] = time.split(':');
        const end = new Date();
        end.setHours(parseInt(hours), parseInt(minutes), 0, 0);

        const diff = end.getTime() - now.getTime();

        if (diff > 0) {
          // Shift still running — countdown
          this.remainingTime = diff;
          this.isOvertime = false;
          this.overtimeMs = 0;
        } else {
          // Shift has ended — count up overtime
          this.remainingTime = 0;
          this.isOvertime = true;
          this.overtimeMs = Math.abs(diff);
        }
      }, 1000);
    },
    reloadTheTimerData() {
      let timerData = JSON.parse(localStorage.getItem("timer"));
      this.showTimer = !!(timerData && timerData.type == "issue");
    },
    timeConvert(type, min) {
      return (min / 60).toFixed(2);
    },
    async goIssueView(value) {
      try {
        let res = await IssueService.findAllIssues({ excelRef: JSON.stringify(value) });
        if (res && res.length > 0) {
          this.$store.dispatch("setIssueInView", res[0].id);
        }
      } catch (e) {
        console.log(e);
      }
    },
    changeDate(direction) {
      let startDate = moment(this.dates[0]);
      let endDate = this.dates[1] ? moment(this.dates[1]) : moment(this.dates[0]);
      const maxDate = moment();
      const maxGap = maxDate.diff(endDate, "days");
      const rangeGap = endDate.diff(startDate, "days");
      const delta = rangeGap > 0 ? rangeGap : 1;
      if (direction === "previous") {
        startDate.subtract(delta, "days");
        endDate.subtract(delta, "days");
      } else if (direction === "next" && maxGap > 0) {
        startDate.add(delta, "days");
        endDate.add(delta, "days");
      }
      this.dates = [startDate.toDate(), endDate.toDate()];
      this.getTimeSheetData();
      this.getTimeCardDetails();
    },
    onSelectDate() {
      const calendar = this.$refs.dateSelect ?? null;
      if (Array.isArray(calendar.modelValue) && lodash.some(calendar.modelValue, (item) => lodash.isNull(item))) {
        calendar.overlayVisible = false;
      }
    },
    moment,
    async getReporterUserData() {
      try {
        let res = await UserService.getReporterUserData();
        this.users = res ? res : [];
        this.users.unshift({ id: this.selectedUser.id, name: "My Space" });
        this.loading = false;
      } catch (e) {
        this.users = [];
        console.log(e);
      }
    },
    dateChanged() {
      this.getTimeSheetData();
      this.getTimeCardDetails();
    },
    checkTimer() {
      let timerData = JSON.parse(localStorage.getItem("timer"));
      this.showTimer = !!(timerData && timerData.type == "issue");
    },
    userSelected($item) {
      this.selectedUser = $item;
      this.getTimeSheetData();
      this.getTimeCardDetails();
    },
    async getUsers() {
      let $wsDefaults = localStorage.getItem("_WS_DEFAULTS_");
      if ($wsDefaults) $wsDefaults = JSON.parse($wsDefaults);
      if ($wsDefaults && $wsDefaults.workspace && $wsDefaults.workspace.id) {
        this.users = await UserService.getUsersForReview({ work_space_id: $wsDefaults.workspace.id });
      }
    },
    async getTimeCardDetails() {
      let $condn = {};
      if (this.dates.length == 2) {
        $condn.from_date = moment(this.dates[0]).format("YYYY-MM-DD");
        $condn.to_date = moment(this.dates[1]).format("YYYY-MM-DD");
      } else if (this.dates.length == 1) {
        $condn.from_date = moment(this.dates[0]).format("YYYY-MM-DD");
        $condn.to_date = moment(this.dates[0]).format("YYYY-MM-DD");
      }
      $condn.user_id = this.selectedUser.id;
      let res = [];
      try {
        res = await TimeSheetService.getDashboardPersonal($condn);
      } catch {
        res = [];
      }
      if (res.length > 0) {
        this.dashBoardCardData.map((k) => (k.hour = 0));
        this.dashBoardCardData.forEach((f) => {
          let data = res.filter((t) => t.task_type.toLowerCase() == f.code.toLowerCase());
          if (data.length > 0) {
            f.hour = data[0].hours && data[0].hours.toFixed(2);
            f.estimate = data[0].estimate && data[0].estimate.toFixed(2);
          }
        });
      } else {
        this.dashBoardCardData.forEach((f) => {
          f.hour = 0;
          f.estimate = 0;
        });
      }
    },
    async getTimeSheetData() {
      let $condn = {};
      if (this.dates.length == 2) {
        $condn.from_date = moment(this.dates[0]).format("YYYY-MM-DD");
        $condn.to_date = moment(this.dates[1]).format("YYYY-MM-DD");
      } else if (this.dates.length == 1) {
        $condn.from_date = moment(this.dates[0]).format("YYYY-MM-DD");
        $condn.to_date = moment(this.dates[0]).format("YYYY-MM-DD");
      }
      $condn.resource_id = this.selectedUser.id;
      let $dashboardData = await TimeSheetService.getAll($condn);
      this.timeSheetData = $dashboardData;
      console.table(this.timeSheetData[0]);
    },
    async getTotalHours() {
      let res = await TimeSheetService.findAll();
      for (let i = 0; i < res.length; i++) {
        var now = new Date(res[i].end_date).getTime();
        var then = new Date(res[i].start_date).getTime();
        var diff = now - then;
        var seconds = Math.floor(diff / 1000) % 60;
        var minutes = Math.floor(diff / (1000 * 60)) % 60;
        var hours = Math.floor(diff / (1000 * 60 * 60));
        var formattedTime =
          hours.toString().padStart(2, "0") + ":" +
          minutes.toString().padStart(2, "0") + ":" +
          seconds.toString().padStart(2, "0");
        var format = formattedTime == "NaN:NaN:NaN" ? "00:00:00" : formattedTime;
        this.totalHours.push(format);
        this.timeSheetData.forEach((e, index) => {
          e.totalHours = this.totalHours[index];
        });
      }
    },
    loadColumns() {
      this.columns = [
        { field: "created", header: "Date", style: { width: "100px", color: "" }, toolTip: "Code", sortfield: true, type: "datetime" },
        { field: "project_name", header: "Project", style: { width: "100px", color: "" }, toolTip: "Name", sortfield: true },
        { field: "task_name", header: "Task", style: { width: "100px", color: "" }, toolTip: "Modified", sortfield: true },
        { field: "start_date", header: "Started by", style: { width: "100px", color: "" }, toolTip: "Modified", sortfield: true, type: "datetime" },
        { field: "end_date", header: "Finished at", style: { width: "100px", color: "" }, toolTip: "Code", sortfield: true, type: "datetime" },
        { field: "actual_hrs", header: "Total hours", style: { width: "100px", color: "" }, toolTip: "Modified", sortfield: true },
      ];
    },
    addActivity() {
      this.dialog = true;
      this.size = commonService.largeDialog();
    },
    getIssueTimerData() {
      this.issueId = localStorage.getItem("currentIsseId");
    },
    reload() {
      this.dialog = false;
      this.reloadTheActivity = true;
    },
    setDashboardWidthMode(mode) {
      this.dashboardWidthMode = this.dashboardWidthMode === mode ? null : mode;
    },
  },
  computed: {
    desktopLayoutClass() {
      return {
        "dash-desktop--assigned-expanded": this.dashboardWidthMode === "assigned-expanded",
        "dash-desktop--timesheet-expanded": this.dashboardWidthMode === "timesheet-expanded",
      };
    },
    isInsightHidden() {
      return ["assigned-expanded", "timesheet-expanded"].includes(this.dashboardWidthMode);
    },
    circumference() {
      return 2 * Math.PI * this.radius;
    },
    progress() {
      const start = new Date(this.workTimingTrack.start_time).getTime();
      const end = new Date(this.workTimingTrack.end_time).getTime();
      const now = new Date().getTime();

      return Math.min((now - start) / (end - start), 1);
    },
    dashOffset() {
      return this.circumference * (1 - this.progress);
    },
    userInitials() {
      if (!this.selectedUser) return "U";
      const f = this.selectedUser.first_name ? this.selectedUser.first_name[0] : "";
      const l = this.selectedUser.last_name ? this.selectedUser.last_name[0] : "";
      return (f + l).toUpperCase() || "U";
    },
    timeOfDay() {
      const h = new Date().getHours();
      if (h < 12) return "morning";
      if (h < 17) return "afternoon";
      return "evening";
    },
    getInBetweenTime() {
      return this.$store.getters.getInBetweenTime;
    },
    reloadTheData() {
      return this.$store.getters.getReloadTheIssue;
    },
    getRefresh() {
      return this.$store.getters.getRefreshTimeSheet;
    },
    changeLocalStore() {
      return localStorage.getItem("hello");
    },
    getTimerHide() {
      return this.$store.getters.getHideTimer;
    },
    getTimesheetDate() {
      return this.$store.getters.getTimesheetDate;
    },
    getSelectedDashboardUser() {
      return this.$store.getters.getSelectedDashboardUser;
    },
    formattedDate() {
      return this.dates;
    },
    formattedTime() {
      const options = { hour: "numeric", minute: "numeric", hour12: true };
      return this.date.toLocaleTimeString("en-US", options);
    },
    refreshProject() {
      return this.$store.getters.getStartIssueId;
    },

    formattedWorkTime() {
      let totalSeconds = Math.floor(this.remainingTime / 1000);

      let hours = Math.floor(totalSeconds / 3600);
      let minutes = Math.floor((totalSeconds % 3600) / 60);
      let seconds = totalSeconds % 60;

      return `${hours}:${minutes}:${seconds}`;
    }
  },
};
</script>

<style scoped>
.dash-body {
  padding: 12px 10px;
  background: #f1f5f9;
  min-height: calc(100vh - 64px);
  box-sizing: border-box;
}

/* ── shared card ── */
.dash-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e9edf2;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  overflow: hidden;
}

/* ── DESKTOP (lg+) ── */
.dash-desktop {
  display: flex;
  gap: 10px;
  height: calc(100vh - 88px);
  overflow: hidden;
  min-width: 0;
}

.dash-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
  min-width: 0;
  transition: flex 0.24s ease, width 0.24s ease, min-width 0.24s ease;
}

.dash-col--assigned {
  flex: 3;
  min-width: 280px;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e9edf2;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  position: relative;
}

.dash-col--center {
  flex: 3.5;
  min-width: 320px;
}

.dash-col--right {
  flex: 3;
  min-width: 0;
  overflow: hidden;
}

.dash-desktop--assigned-expanded,
.dash-desktop--timesheet-expanded {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
}

.dash-desktop--assigned-expanded {
  grid-template-columns: minmax(320px, 4.6fr) minmax(320px, 2.8fr) minmax(0, 2fr);
}

.dash-desktop--timesheet-expanded {
  grid-template-columns: minmax(280px, 3fr) minmax(320px, 4.5fr) minmax(160px, 2fr);
}

.dash-desktop--timesheet-expanded.dash-desktop--shift-active {
  grid-template-columns: minmax(280px, 3fr) minmax(280px, 3.5fr) minmax(160px, 2.5fr);
}

.dash-desktop--assigned-expanded .dash-col--assigned,
.dash-desktop--timesheet-expanded .dash-col--assigned {
  grid-column: 1;
  grid-row: 1 / 3;
}

.dash-desktop--assigned-expanded .dash-col--center,
.dash-desktop--assigned-expanded .dash-col--right,
.dash-desktop--timesheet-expanded .dash-col--center,
.dash-desktop--timesheet-expanded .dash-col--right {
  display: contents;
}

.dash-desktop--assigned-expanded .dash-stats-panel,
.dash-desktop--timesheet-expanded .dash-stats-panel {
  grid-column: 3;
  grid-row: 1;
}

.dash-desktop--assigned-expanded .dash-timesheet-card,
.dash-desktop--timesheet-expanded .dash-timesheet-card {
  grid-column: 2 / 4;
  grid-row: 2;
  min-height: 0;
}

.dash-desktop--assigned-expanded .dash-col--center > .dash-timer-card,
.dash-desktop--assigned-expanded .dash-col--center > .timer-wrapper,
.dash-desktop--timesheet-expanded .dash-col--center > .dash-timer-card,
.dash-desktop--timesheet-expanded .dash-col--center > .timer-wrapper {
  grid-column: 2;
  grid-row: 1;
  min-width: 0;
}

.dash-panel-tools {
  position: absolute;
  top: 50%;
  right: 8px;
  z-index: 12;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transform: translateY(-50%);
}

.dash-panel-tools--timesheet {
  right: 10px;
}

.dash-panel-tool {
  width: 30px;
  height: 30px;
  border: 1px solid rgba(60, 90, 170, 0.18);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.92);
  color: #3C5AAA;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.1);
  transition: background 0.16s ease, border-color 0.16s ease, transform 0.16s ease;
}

.dash-panel-tool:hover,
.dash-panel-tool--active {
  background: #eef2ff;
  border-color: rgba(60, 90, 170, 0.38);
  transform: translateY(-1px);
}

.dash-timer-card {
  flex-shrink: 0;
}

.dash-timesheet-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: width 0.24s ease, align-self 0.24s ease;
}

.dash-timesheet-card--full {
  flex: 1;
}

/* ── stat cards 2x2 grid ── */
.dash-stats-panel {
  width: 100%;
  max-width: 520px;
  flex-shrink: 0;
  transition: max-width 0.24s ease;
}

.dash-desktop--timesheet-expanded .dash-stats-panel {
  width: 520px;
  max-width: 100%;
}

.dash-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  flex-shrink: 0;
  width: 100%;
}

.dash-stats-grid--vertical {
  grid-template-columns: 1fr;
  width: 100%;
}

.dash-stat-card {
  background: #ffffff;
  border-radius: 14px;
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  cursor: default;
  position: relative;
  overflow: hidden;
}

.dash-stat-card--vertical {
  background: #ffffff;
  border-radius: 14px;
  padding: 23px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  cursor: default;
  position: relative;
  overflow: hidden;
}

.dash-stat-card__accent {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3C5AAA, #7986cb);
  opacity: 0;
  transition: opacity 0.22s;
  border-radius: 14px 14px 0 0;
}

.dash-stat-card--hovered {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(60,90,170,0.14);
}
.dash-stat-card--hovered .dash-stat-card__accent { opacity: 1; }

.dash-stat-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dash-stat-card__content {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.dash-stat-card__label {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
}

.dash-stat-card__value {
  display: flex;
  align-items: baseline;
  gap: 3px;
  flex-wrap: wrap;
}

.val-main { font-size: 20px; font-weight: 800; color: #1e293b; line-height: 1; }
.val-sep  { font-size: 13px; color: #cbd5e1; }
.val-est  { font-size: 13px; font-weight: 600; color: #64748b; }
.val-unit { font-size: 10px; color: #94a3b8; font-weight: 500; margin-left: 2px; }

.dash-stat-card__bubble {
  position: absolute;
  bottom: -18px; right: -18px;
  width: 60px; height: 60px;
  border-radius: 50%;
  opacity: 0.15;
  pointer-events: none;
}

/* ── timezone card ── */
.dash-tz-card {
  padding: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

/* ── insights ── */
.dash-insights {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
}

.dash-insight-card {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* ── TABLET (md) ── */
.dash-tablet {
  display: flex;
  gap: 10px;
  height: calc(100vh - 88px);
}

.dash-tablet__left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
}

.dash-tablet__right {
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
}

/* ── MOBILE (sm and down) ── */
.dash-mobile {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 16px;
  overflow-y: auto;
  max-height: calc(100vh - 88px);
}

.dash-mobile .dash-stats-grid {
  grid-template-columns: 1fr 1fr;
}

.dash-mobile .dash-card {
  min-height: 200px;
}

.dash-mobile .dash-timer-card {
  min-height: auto;
}

.timer-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 0;
}


/* ── Scrollbar ── */
::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }

/* Calendar override */
>>> .p-calendar .p-inputtext {
  flex: 0;
  width: 2%;
  position: relative;
  left: 258px;
  z-index: 1;
  background: transparent;
  border: none;
}

>>> .p-inputtext:enabled:focus {
  outline: none;
  border: none;
  box-shadow: none;
  background: transparent;
}
</style>
